import { useEffect, useMemo, useState } from "react";
import {
  ghostBtnClass,
  inputClass,
  mfgSolidDangerBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { ComposeNode } from "@/lib/manufacturing/composePlan";
import { haystackMatchesCommaSearch } from "@/lib/manufacturing/commaDelimitedSearch";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";
import {
  COMPOSE_MATCH_HELP,
  componentListHaystack,
  matchSummary,
} from "./composeMatchDeskHelpers";

/** Same filter used by auto-advance after a match. */
export function filterComposeComponents(
  components: readonly ComposeNode[],
  componentQuery: string,
  hideMatched: boolean,
): ComposeNode[] {
  return components.filter((n) => {
    if (hideMatched && n.match.status === "matched") return false;
    if (!haystackMatchesCommaSearch(componentListHaystack(n), componentQuery)) return false;
    return true;
  });
}

export function ComposeMatchDeskComponentList({
  components,
  products,
  selectedNodeId,
  recipesById,
  statusHelp,
  helpOpen,
  onToggleHelp,
  componentQuery,
  onComponentQuery,
  hideMatched,
  onHideMatched,
  onSelect,
  onBulkMarkTheoretical,
  onBulkSplitStyles,
}: {
  components: readonly ComposeNode[];
  products: readonly ComposeNode[];
  selectedNodeId: string | null;
  recipesById: ReadonlyMap<string, MfgRecipeSummary>;
  statusHelp?: string;
  helpOpen: boolean;
  onToggleHelp: () => void;
  componentQuery: string;
  onComponentQuery: (v: string) => void;
  hideMatched: boolean;
  onHideMatched: (v: boolean) => void;
  onSelect: (id: string) => void;
  onBulkMarkTheoretical?: (nodeIds: string[]) => void;
  onBulkSplitStyles?: (nodeIds: string[]) => void;
}) {
  const filtered = filterComposeComponents(components, componentQuery, hideMatched);
  const filteredIds = useMemo(() => filtered.map((n) => n.id), [filtered]);
  const [checkedIds, setCheckedIds] = useState<string[]>([]);

  useEffect(() => {
    const visible = new Set(filteredIds);
    setCheckedIds((prev) => prev.filter((id) => visible.has(id)));
  }, [filteredIds]);

  const allFilteredChecked =
    filteredIds.length > 0 && filteredIds.every((id) => checkedIds.includes(id));

  function toggleChecked(id: string) {
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function toggleAllFiltered() {
    setCheckedIds(allFilteredChecked ? [] : [...filteredIds]);
  }

  const checkedCount = checkedIds.length;

  return (
    <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
      <div className="relative flex items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Shared components ({filtered.length}
          {filtered.length !== components.length ? `/${components.length}` : ""})
        </div>
        <button
          type="button"
          className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 text-[11px] font-semibold text-slate-600 hover:bg-slate-100 dark:border-zinc-600 dark:text-slate-300 dark:hover:bg-zinc-800"
          aria-label="Compose match help"
          aria-expanded={helpOpen}
          onClick={onToggleHelp}
        >
          ?
        </button>
        {helpOpen ? (
          <div className="absolute right-0 top-7 z-20 w-72 rounded-lg border border-slate-200 bg-white p-3 text-xs leading-relaxed text-slate-700 shadow-lg dark:border-zinc-700 dark:bg-zinc-900 dark:text-slate-300">
            {statusHelp ? <p className="mb-2">{statusHelp}</p> : null}
            <p>{COMPOSE_MATCH_HELP}</p>
            <p className="mt-2 text-slate-500">
              Check multiple parts to Mark theoretical or Split styles in bulk. Filter by slot/size
              (commas AND).
            </p>
          </div>
        ) : null}
      </div>

      <input
        className={`${inputClass} text-xs`}
        value={componentQuery}
        onChange={(e) => onComponentQuery(e.target.value)}
        placeholder="Filter components — floor, 10x12…"
      />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600 dark:text-slate-300">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={hideMatched}
            onChange={(e) => onHideMatched(e.target.checked)}
          />
          Hide matched
        </label>
        {filteredIds.length > 0 ? (
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={allFilteredChecked}
              onChange={toggleAllFiltered}
            />
            Select filtered ({filteredIds.length})
          </label>
        ) : null}
      </div>

      {checkedCount > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 dark:border-zinc-700 dark:bg-zinc-900/60">
          <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
            {checkedCount} selected
          </span>
          {onBulkMarkTheoretical ? (
            <button
              type="button"
              className={`${mfgSolidDangerBtnClass} !px-2 !py-0.5 !text-[11px]`}
              title="Mark selected theoretical"
              onClick={() => {
                onBulkMarkTheoretical(checkedIds);
                setCheckedIds([]);
              }}
            >
              Mark theoretical
            </button>
          ) : null}
          {onBulkSplitStyles ? (
            <button
              type="button"
              className={`${ghostBtnClass} !px-2 !py-0.5 !text-[11px]`}
              title="Split each selected shared part by style"
              onClick={() => {
                onBulkSplitStyles(checkedIds);
                setCheckedIds([]);
              }}
            >
              Split styles
            </button>
          ) : null}
          <button
            type="button"
            className={`${ghostBtnClass} !px-2 !py-0.5 !text-[11px]`}
            onClick={() => setCheckedIds([])}
          >
            Clear
          </button>
        </div>
      ) : null}

      {filtered.length === 0 ? (
        <p className="text-xs text-slate-500">No components match these filters.</p>
      ) : (
        filtered.map((n) => {
          const checked = checkedIds.includes(n.id);
          return (
            <div
              key={n.id}
              className={`flex w-full items-start gap-2 rounded-lg border px-2 py-1.5 text-left text-xs ${
                selectedNodeId === n.id
                  ? "border-indigo-400 bg-indigo-50 dark:border-indigo-600 dark:bg-indigo-950/40"
                  : n.match.status === "matched"
                    ? "border-emerald-300 dark:border-emerald-800"
                    : n.match.status === "theoretical"
                      ? "border-amber-300 dark:border-amber-800"
                      : "border-slate-200 dark:border-zinc-700"
              }`}
            >
              <input
                type="checkbox"
                className="mt-0.5 shrink-0"
                checked={checked}
                aria-label={`Select ${n.code}`}
                onChange={() => toggleChecked(n.id)}
                onClick={(e) => e.stopPropagation()}
              />
              <button type="button" className="min-w-0 flex-1 text-left" onClick={() => onSelect(n.id)}>
                <div className="font-medium">{n.code}</div>
                <div className="text-slate-500">
                  {n.slotKey}
                  {n.variantKey ? ` · ${n.variantKey}` : ""} · {matchSummary(n, recipesById)}
                </div>
              </button>
            </div>
          );
        })
      )}

      <div className="pt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Products ({products.length}) — spot-check
      </div>
      {products.slice(0, 40).map((n) => (
        <button
          key={n.id}
          type="button"
          className={`block w-full rounded-lg border px-2 py-1.5 text-left text-xs ${
            selectedNodeId === n.id
              ? "border-indigo-400 bg-indigo-50 dark:border-indigo-600 dark:bg-indigo-950/40"
              : "border-slate-200 dark:border-zinc-700"
          }`}
          onClick={() => onSelect(n.id)}
        >
          <div className="font-medium">{n.code}</div>
          <div className="text-slate-500">{n.slots.length} slots</div>
        </button>
      ))}
    </div>
  );
}
